import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Pane from 'components/Common/Pane'
import LayoutContainer from 'components/Common/LayoutContainer'
import { TourV2ContentSections } from 'constants/tours'
import Heading from 'LuxKit/Typography/Heading'
import clsx from 'clsx'
import zIndex from 'styles/tools/z-index'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { HeaderHeightContext } from 'contexts/HeaderStateContext'
import { getImmersiveTripId } from 'tripPlanner/selectors'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import TabButton from 'LuxKit/Tabs/TabButton'
import { centreElementInView } from 'lib/window/scrollUtils'
import TextButton from 'LuxKit/Button/TextButton'
import useTourV2PurchasableOptionDetails from 'hooks/TourV2/useTourV2PurchasableOptionDetails'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { getIsTourOfferPurchasable } from 'selectors/TourV2/tourV2OfferPageSelectors'
import type { ProductPalette } from 'contexts/ProductPaletteContext'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import PriceRowPriceWithCaption from 'LuxKit/PricePoints/PriceRowPriceWithCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import TourV2DepartureMonthsCarousel from '../Departures/TourV2DepartureMonthsCarousel'
import PriceRowLuxPlusPriceWithCaption from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPriceWithCaption'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { getTourPricePerDay, getTourV2VariationDuration } from 'lib/tours/tourUtils'
import { getTourPriceMode } from 'selectors/TourV2/toursSelectors'
import { useAppHeaderObserver } from 'hooks/useAppLayoutObservers'
import useTourV2ContentSectionLabels from 'hooks/TourV2/useTourV2ContentSectionLabels'

const StickyPane = styled(Pane)`
  position: fixed;
  width: 100vw;
  z-index: ${zIndex.stickySubHeader};
  top: 0;
  transition: transform 300ms ease-in-out;
  /* Move offscreen by an extra 10px so the shadow isn't visible when header is hidden */
  transform: translateY(calc(-100% - 10px));

  &.visible {
    transform: translateY(0);
  }
`

const TabHeadingContainer = styled(LayoutContainer)`
  ${mediaQueryUp.desktop} {
    padding-top: ${rem(20)};

    &.hide {
      display: none;
    }
  }
`

const MonthHeadingContainer = styled(LayoutContainer)`
  display: none;
  padding-top: ${rem(20)};
  padding-bottom: ${rem(20)};

  ${mediaQueryUp.desktop} {
    &.show {
      display: block;
    }
  }
`

const HeaderTitle = styled(Heading)`
  flex-grow: 1;
  align-self: stretch;
`

const PricingContainer = styled(CSSBreakpoint)`
  flex-shrink: 0;
`

interface Props {
  activeSection?: TourV2ContentSections
  contentSections: Array<TourV2ContentSections>
  onScrollTo: (section: TourV2ContentSections) => void
  purchasableOption?: App.Tours.TourV2OfferPurchasableOption
  title: string
  visible: boolean
  buttonPaletteValue: ProductPalette
  isOfferPurchasable: boolean
  monthCarouselInView?: boolean
  departures: Array<App.Tours.TourV2OfferDeparture>
  variation: App.Tours.TourV2OfferVariation
}

function TourV2StickyHeader(props: Props) {
  const {
    activeSection,
    contentSections,
    isOfferPurchasable,
    onScrollTo,
    purchasableOption,
    title,
    visible,
    buttonPaletteValue,
    monthCarouselInView,
    departures,
    variation,
  } = props

  const intl = useIntl()
  const tabButtonsRef = useRef<Record<string, HTMLButtonElement | null>>({})
  const ref = useRef<HTMLDivElement>(null)
  const contentSectionLabels = useTourV2ContentSectionLabels()
  useAppHeaderObserver(ref, visible ? 'sticky' : 'hidden')

  useEffect(() => {
    if (activeSection) {
      centreElementInView(tabButtonsRef.current[activeSection])
    }
  }, [activeSection])

  const tourPriceMode = useAppSelector(getTourPriceMode)

  const duration = useMemo(() => {
    return getTourV2VariationDuration(variation)
  }, [variation])
  const purchasableOptionDetails = useTourV2PurchasableOptionDetails(purchasableOption)
  const pricePerDayDetails = useMemo(() => {
    if (!purchasableOptionDetails || tourPriceMode === 'total') return
    return getTourPricePerDay(purchasableOptionDetails.pricePerPerson, purchasableOptionDetails.memberPricePerPerson, duration)
  }, [purchasableOptionDetails, duration, tourPriceMode])

  const isLuxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)

  const fromLabel = intl.formatMessage({
    id: 's_ViK7RUubwrVxRyvq7A',
    defaultMessage: 'From',
    description: 'Pricing label prefix before tour sticky header price',
  })
  const personSaleUnit = intl.formatMessage({
    id: 'Q2ezi_9kiNLGaK655HHA',
    defaultMessage: 'person',
    description: 'Per person sale unit label in tour sticky header in the tours section',
  })
  const dayPerPersonSaleUnit = intl.formatMessage({
    id: 'FBEeklDvGqvb3CygNDkf',
    defaultMessage: 'day per person',
    description: 'Per day per person sale unit label in tour sticky header',
  })

  const displayMemberPrice = useMemo(() => (purchasableOptionDetails?.memberPricePerPerson || 0) > 0 && isLuxPlusEnabled, [purchasableOptionDetails, isLuxPlusEnabled])

  const currentTripId = useAppSelector(getImmersiveTripId)
  const { heights: { tripPlannerImmersive: tripPlannerImmersiveHeaderHeight } } = useContext(HeaderHeightContext)

  const offset = currentTripId ? tripPlannerImmersiveHeaderHeight : 0

  const scrollToDepartures = useCallback(() => {
    onScrollTo(TourV2ContentSections.DEPARTURES)
  }, [onScrollTo])

  const scrollToDepartureDates = useCallback(() => {
    onScrollTo(TourV2ContentSections.DEPARTURE_DATES)
  }, [onScrollTo])

  // we also only show them on desktop, but that's done in CSS
  const showMonthFilters = activeSection === TourV2ContentSections.DEPARTURES && !monthCarouselInView

  return (
    <ProductPaletteProvider palette={buttonPaletteValue}>
      <StickyPane
        ref={ref}
        shadow="bottom-small"
        style={{ top: offset }}
        className={clsx({ visible })}
        data-testid="tour-v2-sticky-header"
      >
        {
          departures.length > 1 &&
          <MonthHeadingContainer className={clsx({ show: showMonthFilters })}>
            <TourV2DepartureMonthsCarousel
              departures={departures}
              onSelect={scrollToDepartureDates}
            />
          </MonthHeadingContainer>
        }
        <TabHeadingContainer className={clsx({ hide: showMonthFilters })}>
          <Group direction="horizontal" gap={12} horizontalAlign="space-between" verticalAlign="center">
            <CSSBreakpoint min="tablet">
              <HeaderTitle variant="heading5" colour="neutral-one">{title}</HeaderTitle>
            </CSSBreakpoint>
            <Group direction="horizontal" gap={12}>
              <PricingContainer min="tablet">
                {!!purchasableOptionDetails && <>
                  {
                    !pricePerDayDetails && <>
                      {!displayMemberPrice && <PriceRowPriceWithCaption
                        size="M"
                        caption={fromLabel}
                        price={purchasableOptionDetails.pricePerPerson}
                        saleUnit={personSaleUnit}
                      />}
                      {displayMemberPrice && <PriceRowLuxPlusPriceWithCaption
                        size="M"
                        caption={fromLabel}
                        memberPrice={purchasableOptionDetails.memberPricePerPerson}
                        saleUnit={personSaleUnit}
                      />}
                    </>
                  }
                  {
                    pricePerDayDetails && <>
                      {displayMemberPrice && <PriceRowLuxPlusPriceWithCaption
                        size="M"
                        caption={fromLabel}
                        memberPrice={pricePerDayDetails.memberPricePerPersonPerDay}
                        saleUnit={dayPerPersonSaleUnit}
                      />}
                      {!displayMemberPrice && <PriceRowPriceWithCaption
                        size="M"
                        caption={fromLabel}
                        price={pricePerDayDetails.pricePerPersonPerDay}
                        saleUnit={dayPerPersonSaleUnit}
                      />}
                    </>
                  }
                  <PriceRowCaption>
                    {purchasableOptionDetails.roomTypeName}
                  </PriceRowCaption>
                </>}

                {!isOfferPurchasable && <Heading variant="heading5" align="center">
                  {intl.formatMessage({
                    id: 'ienbcviqaM_Wz6-hur64',
                    defaultMessage: 'Sold Out',
                    description: 'Status text when offer is sold out on the tour offer detail page',
                  })}
                </Heading>}
              </PricingContainer>

              {isOfferPurchasable && <CSSBreakpoint min="tablet">
                <TextButton kind="primary" onClick={scrollToDepartures}>
                  {intl.formatMessage({
                    id: '8jowxjaBSFwrFBgdcnmz',
                    defaultMessage: 'Check Availability',
                    description: 'Action button label to check availability in the tours section',
                  })}
                </TextButton>
              </CSSBreakpoint>}
            </Group>
          </Group>
          <TabButtonsGroup>
            {contentSections.map(contentSection => <TabButton
              key={contentSection}
              ref={(element) => { tabButtonsRef.current[contentSection] = element }}
              size="medium"
              isActive={contentSection === activeSection}
              onClick={() => onScrollTo(contentSection)}
            >
              {contentSectionLabels[contentSection]}
            </TabButton>)}
          </TabButtonsGroup>
        </TabHeadingContainer>
      </StickyPane>
    </ProductPaletteProvider>
  )
}

type MappedProps = Pick<Props, 'isOfferPurchasable' | 'departures'>

export default withOfferPageState<MappedProps, Props>((offerPageState) => ({
  departures: offerPageState.tourV2Variation!.departures,
  isOfferPurchasable: getIsTourOfferPurchasable(offerPageState),
}), TourV2StickyHeader)
